import ArrowLeftThinIcon from '@assets/icons/arrow-left-thin.svg';
import CalendarIcon from '@assets/icons/calendar.svg';
import ClockIcon from '@assets/icons/clock.svg';
import i18n from '@/utils/i18n';
import moment from 'moment-timezone';
import React from 'react';
import { Button } from '@/components/button';
import { Calendar } from '@usepepper/ui/drafts/calendar';
import { createStyles } from '@/contexts/theme-context';
import {
  MINUTE_INTERVAL,
  ReminderTimestamp,
  useNoteReminderForm,
} from '@/hooks/use-note-reminder-form';
import { Moment } from 'moment/moment';
import { Platform, Text, useWindowDimensions, View } from 'react-native';
import { TimePickerModal } from '@/components/time-picker/time-picker-modal';
import { ToggleSetting } from './toggle-setting';
import { TYPOGRAPHY } from '@/styles';
import { useToasts } from '@/contexts/toast-context';

const REMINDER_SHEET_RELATIVE_HEIGHT = 0.75;

export interface BaseNoteReminderProps {
  onClose: () => void;
  onSave: () => void;
  onSaveEnabled: boolean;
}

export interface NoteReminderFormProps extends BaseNoteReminderProps {
  buttonRef?: React.RefObject<View | null>;
  isDateDisabled: (date: Moment) => boolean;
  noteReminder?: ReminderTimestamp;
  onChange: (reminder?: ReminderTimestamp) => void;
}

interface DateToggleSelectorProps {
  date?: Date;
  dateToggleSubtitle?: string;
  isDateDisabled: (date: Moment) => boolean;
  isDateReminderCalendarVisible: boolean;
  isDateReminderEnabled: boolean;
  onClickDateSetting: () => void;
  onDateChange: (date: Moment) => void;
  onDateSwitchChange: (isDateOn: boolean) => void;
}

const DateToggleSelector: React.FC<DateToggleSelectorProps> = ({
  date,
  isDateReminderEnabled,
  isDateReminderCalendarVisible,
  dateToggleSubtitle,
  onClickDateSetting,
  onDateSwitchChange,
  onDateChange,
  isDateDisabled,
}) => {
  const [styles, theme] = useStyles();

  return (
    <>
      <ToggleSetting
        icon={
          <CalendarIcon
            fill={theme.colors.transparent}
            height={16}
            width={16}
          />
        }
        onClick={onClickDateSetting}
        onToggleChange={onDateSwitchChange}
        subtitle={dateToggleSubtitle}
        title={i18n.t('new_note_reminder.date')}
        toggleTestID="note_reminder.date_switch"
        trackingData={{
          name: 'new_note_reminder.date_switch',
          properties: {
            value: !isDateReminderEnabled,
          },
        }}
        value={isDateReminderEnabled}
      />
      {isDateReminderCalendarVisible ? (
        <>
          <Calendar
            activeDate={date ? moment(date) : null}
            isDateDisabled={isDateDisabled}
            onDateChange={onDateChange}
          />
          <View style={styles.divider} />
        </>
      ) : null}
    </>
  );
};

interface TimeToggleSelectorProps {
  isTimeReminderEnabled: boolean;
  isTimeReminderModalVisible: boolean;
  minimumTime?: Date;
  onClickTimeSetting: () => void;
  onTimeChange: (time: Date) => void;
  onTimeHide: (isValid: boolean) => void;
  onTimeSwitchChange: (isTimeOn: boolean) => void;
  time?: Date;
  timeToggleSubtitle?: string;
}

const TimeToggleSelector: React.FC<TimeToggleSelectorProps> = ({
  time,
  isTimeReminderEnabled,
  isTimeReminderModalVisible,
  timeToggleSubtitle,
  onClickTimeSetting,
  onTimeSwitchChange,
  onTimeChange,
  onTimeHide,
  minimumTime = new Date(),
}) => {
  const [, theme] = useStyles();

  return (
    <>
      <ToggleSetting
        icon={
          <ClockIcon
            height={18}
            stroke={theme.sk.color.icon.primary}
            width={18}
          />
        }
        onClick={onClickTimeSetting}
        onToggleChange={onTimeSwitchChange}
        subtitle={timeToggleSubtitle}
        title={i18n.t('new_note_reminder.time')}
        toggleTestID="note_reminder.time_switch"
        trackingData={{
          name: 'new_note_reminder.time_switch',
          properties: {
            value: !isTimeReminderEnabled,
          },
        }}
        value={isTimeReminderEnabled}
      />

      {isTimeReminderModalVisible ? (
        <TimePickerModal
          accessibilityLabel="note_reminder.time_picker_modal"
          confirmButtonTestID="note_reminder.time_picker_confirm_button"
          date={time}
          display={Platform.OS === 'ios' ? 'spinner' : 'default'}
          is24Hour={false}
          isVisible
          minimumDate={minimumTime}
          minuteInterval={MINUTE_INTERVAL}
          onCancel={() => onTimeSwitchChange(false)}
          onConfirm={onTimeChange}
          onHide={onTimeHide}
        />
      ) : null}
    </>
  );
};

const AddNoteReminderHeader: React.FC<BaseNoteReminderProps> = ({
  onSave,
  onSaveEnabled,
  onClose,
}) => {
  const [styles, theme] = useStyles();

  return (
    <View style={styles.reminderHeaderContainer}>
      <Button
        Icon={ArrowLeftThinIcon}
        iconProps={{
          fill: theme.sk.color.icon.primary,
          height: 24,
          width: 24,
        }}
        onPress={onClose}
        rounded
        size="small"
        style={styles.backButton}
        trackingData={{ name: 'note_activity.close_reminder' }}
      />
      <View style={styles.reminderHeader}>
        <Text style={[TYPOGRAPHY.h3, styles.reminderHeaderText]}>
          {i18n.t('new_note_reminder.title')}
        </Text>
      </View>
      <Button
        containerStyle={styles.saveContainer}
        disabled={!onSaveEnabled}
        onPress={onSave}
        size="small"
        style={styles.saveButton}
        trackingData={{ name: 'note_activity.save_reminder' }}
        type="tertiary"
      >
        {i18n.t('save')}
      </Button>
    </View>
  );
};

export const AddNoteReminderSheetContent: React.FC<NoteReminderFormProps> = ({
  onClose,
  onChange,
  noteReminder,
  onSave,
  onSaveEnabled,
  isDateDisabled,
}) => {
  const [styles] = useStyles();
  const { height: screenHeight } = useWindowDimensions();
  const { showErrorToast } = useToasts();

  const {
    isDateReminderEnabled,
    isDateReminderCalendarVisible,
    isTimeReminderEnabled,
    isTimeReminderModalVisible,
    dateToggleSubtitle,
    timeToggleSubtitle,
    onDateChange,
    onDateSwitchChange,
    onClickDateSetting,
    onTimeChange,
    onTimeSwitchChange,
    onClickTimeSetting,
    minimumDate,
  } = useNoteReminderForm(onChange, noteReminder);

  const handleOnHide = (validTime: boolean) => {
    if (!validTime) {
      // If the time is not valid, we reset the time selection
      onTimeSwitchChange(false);
      if (noteReminder?.date) {
        onChange({
          date: noteReminder.date,
          time: undefined,
        });
      } else {
        onChange(undefined);
      }
      showErrorToast(i18n.t('new_note_reminder.error_past_reminder'));
    }
  };

  return (
    <>
      <AddNoteReminderHeader
        onClose={onClose}
        onSave={onSave}
        onSaveEnabled={onSaveEnabled}
      />
      <View
        style={[
          styles.sheet,
          { height: screenHeight * REMINDER_SHEET_RELATIVE_HEIGHT },
        ]}
      >
        {/* Reminder Date */}
        <DateToggleSelector
          date={noteReminder?.date}
          dateToggleSubtitle={dateToggleSubtitle}
          isDateDisabled={isDateDisabled}
          isDateReminderCalendarVisible={isDateReminderCalendarVisible}
          isDateReminderEnabled={isDateReminderEnabled}
          onClickDateSetting={onClickDateSetting}
          onDateChange={onDateChange}
          onDateSwitchChange={onDateSwitchChange}
        />

        {/* Reminder Time */}
        <TimeToggleSelector
          isTimeReminderEnabled={isTimeReminderEnabled}
          isTimeReminderModalVisible={isTimeReminderModalVisible}
          minimumTime={minimumDate}
          onClickTimeSetting={onClickTimeSetting}
          onTimeChange={onTimeChange}
          onTimeHide={handleOnHide}
          onTimeSwitchChange={onTimeSwitchChange}
          time={noteReminder?.time}
          timeToggleSubtitle={timeToggleSubtitle}
        />
      </View>
    </>
  );
};

const useStyles = createStyles((theme) => ({
  backButton: {
    backgroundColor: theme.colors.transparent,
  },
  divider: {
    backgroundColor: theme.sk.color.border.primary,
    height: 1,
    marginHorizontal: 8,
  },
  reminderHeader: {
    justifyContent: 'center',
  },
  reminderHeaderContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  reminderHeaderText: {
    alignSelf: 'center',
    color: theme.sk.color.text.primary,
  },
  saveButton: {
    paddingHorizontal: 0,
  },
  saveContainer: {
    justifyContent: 'center',
  },
  sheet: {
    gap: 16,
  },
}));
